import React from "react";
import styled, { css } from "styled-components/macro";

import { SUMMARY_GRID_COLUMNS } from "./constants";
import {
    fmtNumber,
    fmtSignedHours,
    getInitials,
    getRecoveryColor,
    pluralize,
} from "./format";
import { MonthStatusBar } from "./MonthStatusBar";
import {
    Checkbox,
    CountPill,
    InitialsAvatar,
    Muted,
    ResetButton,
    UnstyledButton,
} from "./index.style";

/**
 * Tableau groupé de la synthèse annuelle
 * @param {{ groups: object[], total: object, highlight: string[], selection: object, onResetFilters: function, loading?: boolean }} props
 */
const SummaryTable = ({
    groups = [],
    total,
    highlight = [],
    selection,
    onResetFilters,
    loading = false,
}) => (
    <Scroll>
        <Table $loading={loading}>
            <HeaderRow>
                <CheckboxButton
                    aria-label="Tout sélectionner"
                    onClick={selection.toggleAll}
                >
                    <Checkbox checked={selection.isAllSelected} />
                </CheckboxButton>
                <span>Tout sélectionner</span>
                <Center>Relevés mensuels</Center>
                <Right>Jours travaillés</Right>
                <Right>Vacances</Right>
                <Right>Télétravail</Right>
                <Right>Récup. annuelle</Right>
                <span />
            </HeaderRow>

            {groups.map((group) => (
                <React.Fragment key={group.id ?? group.name}>
                    <GroupRow>
                        <span />
                        <GroupName>
                            <span>{group.name}</span>
                            <CountPill>{group.count} collab.</CountPill>
                        </GroupName>
                        <span />
                        <Right>{fmtNumber(group.totals.days_worked)} j</Right>
                        <Right>{fmtNumber(group.totals.days_vacation)} j</Right>
                        <Right>{fmtNumber(group.totals.days_telework)} j</Right>
                        <Right>{fmtSignedHours(group.totals.recovery_hours)}</Right>
                        <span />
                    </GroupRow>
                    {group.rows.map((row) => (
                        <SummaryRow
                            key={row.user_id}
                            row={row}
                            highlight={highlight}
                            checked={selection.value.includes(row.user_id)}
                            onToggle={() => selection.toggle(row.user_id)}
                        />
                    ))}
                </React.Fragment>
            ))}

            {!loading && !groups.length && (
                <Empty>
                    <span>Aucun collaborateur ne correspond à vos filtres.</span>
                    <ResetButton onClick={onResetFilters}>
                        Réinitialiser les filtres
                    </ResetButton>
                </Empty>
            )}

            <TotalRow>
                <span />
                <span>
                    Total · {pluralize(total?.count ?? 0, "collaborateur")}
                </span>
                <span />
                <Right>{fmtNumber(total?.days_worked)} j</Right>
                <Right>{fmtNumber(total?.days_vacation)} j</Right>
                <Right>{fmtNumber(total?.days_telework)} j</Right>
                <Right>{fmtSignedHours(total?.recovery_hours)}</Right>
                <span />
            </TotalRow>
        </Table>
    </Scroll>
);

const SummaryRow = ({ row, highlight, checked, onToggle }) => (
    <Row $checked={checked}>
        <CheckboxButton
            aria-label={`Sélectionner ${row.first_name} ${row.last_name}`}
            onClick={onToggle}
        >
            <Checkbox checked={checked} />
        </CheckboxButton>
        <Identity>
            <InitialsAvatar
                src={row.avatar_url}
                initials={getInitials(row.first_name, row.last_name)}
            />
            <IdentityText>
                <Name>
                    {row.first_name} {row.last_name}
                </Name>
                <JobTitle>{row.job_title}</JobTitle>
            </IdentityText>
        </Identity>
        <Center>
            <MonthStatusBar months={row.months} highlight={highlight} />
        </Center>
        <Right $bold>
            {fmtNumber(row.days_worked)} <Muted>j</Muted>
        </Right>
        <Right>
            {fmtNumber(row.days_vacation)}{" "}
            <Muted>/ {fmtNumber(row.days_vacation_entitlement)} j</Muted>
        </Right>
        <Right>
            {fmtNumber(row.days_telework)} <Muted>j</Muted>
        </Right>
        <Right $bold style={{ color: getRecoveryColor(row.recovery_hours) }}>
            {fmtSignedHours(row.recovery_hours)}
        </Right>
        <span />
    </Row>
);

export default SummaryTable;

const Scroll = styled.div`
    overflow-x: auto;
`;

const Table = styled.div`
    min-width: 1040px;
    transition: opacity 0.15s;
    opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
`;

const grid = css`
    display: grid;
    grid-template-columns: ${SUMMARY_GRID_COLUMNS};
    align-items: center;
`;

const HeaderRow = styled.div`
    ${grid};
    padding: 10px 12px;
    font-size: 12px;
    color: #616161;
    font-weight: 600;
    background: #fafafa;
    border-bottom: 1px solid #edebe9;
`;

const GroupRow = styled.div`
    ${grid};
    padding: 10px 12px;
    background: #f5f5f5;
    border-bottom: 1px solid #edebe9;
    font-size: 13px;
    font-weight: 700;
`;

const GroupName = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Row = styled.div`
    ${grid};
    padding: 0 12px;
    height: 56px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;
    background: ${({ $checked }) => ($checked ? "#F0F0FB" : "#fff")};
    &:hover {
        background: #f7f7fd;
    }
`;

const TotalRow = styled.div`
    ${grid};
    padding: 12px;
    font-size: 13px;
    font-weight: 700;
    background: #fafafa;
`;

const CheckboxButton = styled(UnstyledButton)`
    display: flex;
    justify-content: center;
`;

const Identity = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    position: sticky;
    left: 0;
    background: inherit;
    align-self: stretch;
    z-index: 1;
`;

const IdentityText = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const Name = styled.span`
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const JobTitle = styled.span`
    font-size: 11px;
    color: #616161;
`;

const Center = styled.div`
    display: flex;
    justify-content: center;
`;

const Right = styled.span`
    text-align: right;
    font-weight: ${({ $bold }) => ($bold ? 600 : "inherit")};
`;

const Empty = styled.div`
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    color: #616161;
    font-size: 14px;
`;
